import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme } from '../../ui/theme';
import { OUTFITS } from '../../data/outfits';
import { OutfitIconRenderer } from '../../systems/OutfitIconRenderer';
import { svgStringToTexture } from '../../utils/svgToTexture';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';
import type { HairStyle } from '../../types';

export class MemoryScene extends Phaser.Scene {
  private score = 0;
  private toast!: ToastManager;

  constructor() { super('MemoryScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.cameras.main.setBackgroundColor('#fef0f5');
    this.toast = new ToastManager(this);
    this.score = 0;

    this.add.text(GAME_WIDTH/2, 75, '👀 메모리 게임', { fontFamily: Theme.font.family, fontSize: '34px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);
    void this.startRound();

    new Button(this, 100, GAME_HEIGHT-50, '← 종료', () => this.end(), { width: 120, height: 48, style: 'secondary' });
  }

  private async startRound(): Promise<void> {
    const all = OUTFITS.filter(o => o.unlockMethod !== 'starter');
    const shown = [...all].sort(() => Math.random() - 0.5).slice(0, 4);
    const distractors = [...all].filter(o => !shown.includes(o)).sort(() => Math.random() - 0.5).slice(0, 4);
    const allOptions = [...shown, ...distractors].sort(() => Math.random() - 0.5);

    // 단계 1: 보여주기
    const showContainer = this.add.container(0, 0);
    const showLabel = this.add.text(GAME_WIDTH/2, 155, '5초간 외우세요!', { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '700', color: Theme.hex.textMid }).setOrigin(0.5);
    showContainer.add(showLabel);

    // Load & show icons for displayed outfits
    await Promise.all(shown.map(async (o, i) => {
      const x = 360 + i * 200;
      const y = 350;

      // Card background
      const card = this.add.graphics();
      card.fillStyle(parseInt(o.color.slice(1), 16), 0.2);
      card.lineStyle(3, 0x3a2540);
      card.fillRoundedRect(x - 70, y - 70, 140, 140, 10);
      card.strokeRoundedRect(x - 70, y - 70, 140, 140, 10);
      showContainer.add(card);

      // Outfit icon
      const hairStyle = (o.slot === 'hair' && o.hairStyle) ? o.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(o.slot, o.color, hairStyle, o.iconStyle);
      const iconKey = `mem_show_icon_${o.id}`;
      try {
        await svgStringToTexture(this, iconKey, iconSvg);
        if (!this.scene.isActive()) return;
        const icon = this.add.image(x, y - 15, iconKey).setScale(3.2);
        showContainer.add(icon);
      } catch { /* ignore */ }

      const label = this.add.text(x, y + 55, o.name, { fontFamily: Theme.font.family, fontSize: '13px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0.5);
      showContainer.add(label);
    }));

    this.time.delayedCall(5000, () => {
      showContainer.destroy();
      void this.showOptions(shown.map(o => o.id), allOptions);
    });
  }

  private async showOptions(answerIds: string[], options: typeof OUTFITS): Promise<void> {
    this.add.text(GAME_WIDTH/2, 155, '본 옷을 모두 골라주세요!', { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '700', color: Theme.hex.textMid }).setOrigin(0.5);
    const picked = new Set<string>();

    const updateText = this.add.text(GAME_WIDTH/2, 200, `0 / ${answerIds.length}`, { fontFamily: Theme.font.family, fontSize: '18px', fontStyle: '800', color: Theme.hex.pink }).setOrigin(0.5);

    await Promise.all(options.map(async (o, i) => {
      const x = 280 + (i % 4) * 240;
      const y = 320 + Math.floor(i/4) * 200;

      // Card background (starts neutral)
      const card = this.add.graphics();
      card.fillStyle(parseInt(o.color.slice(1), 16), 0.15);
      card.lineStyle(3, 0x3a2540);
      card.fillRoundedRect(x - 80, y - 80, 160, 160, 10);
      card.strokeRoundedRect(x - 80, y - 80, 160, 160, 10);

      // Outfit icon
      const hairStyle = (o.slot === 'hair' && o.hairStyle) ? o.hairStyle as HairStyle : undefined;
      const iconSvg = OutfitIconRenderer.render(o.slot, o.color, hairStyle, o.iconStyle);
      const iconKey = `mem_opt_icon_${o.id}`;
      try {
        await svgStringToTexture(this, iconKey, iconSvg);
        if (!this.scene.isActive()) return;
        this.add.image(x, y - 20, iconKey).setScale(3.5);

        // Hit zone on the icon + card area
        const hit = this.add.rectangle(x, y, 160, 160, 0x000000, 0);
        hit.setInteractive({ useHandCursor: true });
        hit.on('pointerdown', () => {
          if (picked.has(o.id)) return;
          picked.add(o.id);
          if (answerIds.includes(o.id)) {
            this.score += 25;
            card.clear();
            card.fillStyle(0xffd95c, 0.3);
            card.lineStyle(5, 0xffd95c);
            card.fillRoundedRect(x - 80, y - 80, 160, 160, 10);
            card.strokeRoundedRect(x - 80, y - 80, 160, 160, 10);
            AudioManager.playSfx('correct');
            this.toast.show('✓ 정답!', 'gold');
          } else {
            this.score = Math.max(0, this.score - 10);
            card.clear();
            card.fillStyle(0xe54e87, 0.3);
            card.lineStyle(5, 0xe54e87);
            card.fillRoundedRect(x - 80, y - 80, 160, 160, 10);
            card.strokeRoundedRect(x - 80, y - 80, 160, 160, 10);
            AudioManager.playSfx('wrong');
            this.toast.show('❌');
          }
          const correctCount = [...picked].filter(id => answerIds.includes(id)).length;
          updateText.setText(`${correctCount} / ${answerIds.length}`);
          if (correctCount === answerIds.length) {
            this.time.delayedCall(800, () => this.end());
          }
        });
      } catch { /* ignore */ }

      const label = this.add.text(x, y + 75, o.name, { fontFamily: Theme.font.family, fontSize: '13px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0.5);
      void label; // label added to scene directly
    }));
  }

  private end(): void {
    const reward = MiniGameRunner.reportResult('memory', this.score);
    this.cameras.main.fadeOut(200);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.toast.show(`🎉 ${this.score}점! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
      this.time.delayedCall(1800, () => this.scene.start('MiniGameHubScene'));
    });
  }
}
